import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

interface FunnelSlice {
  /** The stage's identity - what the Board's `?column=` link carries. */
  column: string;
  label: string;
  /** `open` | `won` | `lost`. */
  outcome: string;
  count: number;
  contribution: number;
}
interface MonthlyOutcome { month: string; won: number; lost: number; wonContribution: number; }
interface OriginSlice { origin: string; count: number; }

/** What the open pipeline is made of, beside what it is worth. */
interface PipelineHealth {
  openCount: number;
  openWithEstimate: number;
  weightedValue: number;
  openWithForecast: number;
  stalledCount: number;
  stalledValue: number;
  stalledAfterDays: number;
  overdueActionCount: number;
}

interface AnalyticsSummary {
  funnel: FunnelSlice[];
  openPipelineValue: number;
  outcomeTrend: MonthlyOutcome[];
  leadsByOrigin: OriginSlice[];
  pendingApprovals: number;
  health: PipelineHealth;
}

/**
 * Lead Analytics, formerly the Revenue Command Center (06 §Command
 * Center): the profit-goal-first read-only surface. Every number is derived
 * server-side from truth/read models (PR-24) - nothing here is a stored,
 * sourceless UI figure. Drills into the Board (a stage column) and the
 * Approval center.
 *
 * It is no longer the group's landing page: the decision queue it used to
 * host belongs to the Overview (design 3a), which is an inbox. This is a
 * report, and it stopped pretending to be both.
 *
 * ── WHAT IT REPORTS, AND WHY THOSE ────────────────────────────────────────
 *
 * The page answers three questions in the order somebody opening it has
 * them, and nothing else:
 *
 *   What is in play      open pipeline, and the weighted forecast beside it
 *   Are we converting    win rate and the average won deal, over 12 months
 *   What is rotting      leads stalled in a stage, overdue actions, approvals
 *
 * The funnel, the won/lost trend and the origins mix stay below as the
 * detail behind those headlines.
 *
 * ── A NUMBER NOBODY ENTERED IS NOT ZERO ───────────────────────────────────
 *
 * The money here is only as real as the estimates behind it, and a tenant who
 * has not filled in expected contributions used to be told their open
 * pipeline was $0 - which reads as "worthless", not as "unmeasured", and the
 * two are the same glyph on a screen. So every money figure is shown only
 * when something was actually recorded, and carries its coverage ("estimates
 * on 30 of 125") so the reader can tell how much of the pipeline the number
 * speaks for. Same for the forecast, which needs probabilities, and for the
 * win rate, which needs closed leads.
 */
@Component({
  selector: 'app-command-center',
  templateUrl: './command-center.component.html',
  styleUrls: ['./command-center.component.css'],
})
export class CommandCenterComponent implements OnInit {
  summary: AnalyticsSummary | null = null;
  loading = false;
  loadError: string | null = null;

  constructor(private http: HttpClient, private router: Router) {}

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.http.get<AnalyticsSummary>('/api/leads-analytics/summary').subscribe({
      next: res => { this.summary = res; this.loading = false; },
      error: () => {
        this.loadError = 'Failed to load the analysis.';
        this.loading = false;
      },
    });
  }

  // ── What is in play ──

  get health(): PipelineHealth | null {
    return this.summary?.health ?? null;
  }

  /**
   * The full sentence, for anyone who stops on a figure long enough to ask.
   * The card says what a number is; this says how it was worked out.
   *
   * Two entries where there were four. "Open pipeline" and "Expected to win"
   * were removed from the page: both priced the open list rather than
   * reporting it, both depended on an Annual Contribution and a Probability
   * that this tenant's leads largely do not carry, and both spent a headline
   * on a figure whose own caption had to explain how little of the list it
   * spoke for. The two that are left count things that happened.
   */
  readonly explain = {
    won: 'Leads that reached a winning stage this calendar month, and what they '
      + 'are worth — with, underneath, the share of everything that CLOSED in the '
      + 'last 12 months that was won. Leads still being worked are not counted as '
      + 'losses.',
  };

  // ── Are we converting ──

  get wonTwelveMonths(): number {
    return (this.summary?.outcomeTrend ?? []).reduce((sum, m) => sum + m.won, 0);
  }

  get lostTwelveMonths(): number {
    return (this.summary?.outcomeTrend ?? []).reduce((sum, m) => sum + m.lost, 0);
  }

  /** Won as a share of everything that CLOSED, not of everything in the
   *  pipeline: leads still being worked have not lost, and counting them as
   *  losses would make a healthy quarter with a full funnel look like a bad
   *  one. Null while nothing has closed - 0% would be a claim. */
  get winRate(): number | null {
    const closed = this.wonTwelveMonths + this.lostTwelveMonths;
    return closed === 0 ? null : this.wonTwelveMonths / closed;
  }

  /** Average won deal over the same 12 months, from the contribution the
   *  server summed. Null when nothing closed, or when what closed carried no
   *  estimate - dividing by the count would print an average of $0. */
  get averageWonDeal(): number | null {
    const trend = this.summary?.outcomeTrend ?? [];
    const money = trend.reduce((sum, m) => sum + m.wonContribution, 0);
    return this.wonTwelveMonths === 0 || money === 0
      ? null
      : money / this.wonTwelveMonths;
  }

  // ── What is rotting ──

  /** True while anything on the attention row is non-zero, so a clean board
   *  can say so in one line instead of showing three zeros. */
  get needsAttention(): boolean {
    const h = this.health;
    return !!h && (h.stalledCount > 0 || h.overdueActionCount > 0)
      || (this.summary?.pendingApprovals ?? 0) > 0;
  }

  // ── Funnel (only the active-pipeline columns; outcomes shown separately) ──

  /**
  * The stages the server sent, in the order it sent them, minus the two a
  * lead stops in - those are the outcome trend below, not steps you plan
  * through.
  *
  * This used to be a hardcoded list of six names, and it had gone stale: it
  * still held the retired Opportunity vocabulary (Signal, Evidence,
  * ServiceableProfitable, Engage, Pricing), so five of the six rows matched
  * nothing the server returned and drew a permanent zero. A funnel that
  * quietly reports no pipeline is worse than one that reports none at all.
  */
  get funnel(): FunnelSlice[] {
    return (this.summary?.funnel ?? []).filter(slice => slice.outcome === 'open');
  }

  get maxFunnelCount(): number {
    return Math.max(1, ...this.funnel.map(f => f.count));
  }

  get funnelEmpty(): boolean {
    return this.funnel.every(f => f.count === 0);
  }

  funnelWidth(slice: FunnelSlice): string {
    return `${Math.round((slice.count / this.maxFunnelCount) * 100)}%`;
  }

  // ── Outcome trend. The API only returns months that HAD outcomes, so fill
  // the last 12 calendar months for an honest x-axis (and so "this month"
  // really is this month, not just the newest row). Months are UTC to match
  // the server's OutcomeAt grouping. ──

  get trendMonths(): MonthlyOutcome[] {
    const byKey = new Map((this.summary?.outcomeTrend ?? []).map(m => [m.month.slice(0, 7), m]));
    const now = new Date();
    const months: MonthlyOutcome[] = [];
    for (let back = 11; back >= 0; back--) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - back, 1));
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      months.push(byKey.get(key) ?? { month: `${key}-01`, won: 0, lost: 0, wonContribution: 0 });
    }
    return months;
  }

  get thisMonth(): MonthlyOutcome {
    const months = this.trendMonths;
    return months[months.length - 1];
  }

  get hasOutcomes(): boolean {
    return (this.summary?.outcomeTrend ?? []).length > 0;
  }

  get maxTrendCount(): number {
    return Math.max(1, ...this.trendMonths.flatMap(m => [m.won, m.lost]));
  }

  trendHeight(value: number): string {
    return `${Math.round((value / this.maxTrendCount) * 100)}%`;
  }

  private static readonly MonthNames =
    ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  monthLabel(month: string): string {
    // month is "YYYY-MM-01" (DateOnly). Slice it - new Date() would parse as
    // UTC midnight and shift back a month in negative-offset timezones.
    const idx = Number(month.slice(5, 7)) - 1;
    return CommandCenterComponent.MonthNames[idx] ?? month;
  }

  // ── Origins ──

  get origins(): OriginSlice[] {
    return this.summary?.leadsByOrigin ?? [];
  }

  get maxOriginCount(): number {
    return Math.max(1, ...this.origins.map(o => o.count));
  }

  originWidth(o: OriginSlice): string {
    return `${Math.round((o.count / this.maxOriginCount) * 100)}%`;
  }

  // ── Drill-downs. A funnel row deep-links its column; the Board reads
  // ?column= once and opens focused on it (clearable there via its chip). ──

  openBoard(column: string): void {
    this.router.navigate(['/leads'], { queryParams: { column } });
  }

  openApprovals(): void {
    this.router.navigate(['/leads']);
  }

}
